import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  LogOut, Settings, Package, Wrench, HelpCircle, Users, ExternalLink,
  Plus, Trash2, Edit3, Save, X, ChevronDown, ChevronUp, Eye, EyeOff,
} from "lucide-react";
import {
  adminLogin, adminLogout, isAdminLoggedIn,
  getSettings, saveSettings,
  getProducts, addProduct, updateProduct, deleteProduct,
  getServices, addService, updateService, deleteService,
  getFaqs, addFaq, updateFaq, deleteFaq,
  getReferences, addReference, updateReference, deleteReference,
  getAbout, saveAbout,
  compressToBase64,
  exportSiteData,
} from "../lib/store";

// ─── Shared UI helpers ────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0d1b2a]/60 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
const inp = "w-full bg-[#f6f6f6] border border-[#0d1b2a]/10 rounded-md py-2.5 px-3 text-sm focus:outline-none focus:border-[#ffb703] transition-colors";
const ta  = inp + " resize-none";

// ─── Image upload field ───────────────────────────────────────────────────────
function ImageField({ label, value, onChange }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = React.useRef(null);

  async function handleFile(file) {
    if (!file) return;
    setErr(""); setLoading(true);
    try {
      const b64 = await compressToBase64(file);
      onChange(b64);
    } catch {
      setErr("Görsel yüklenemedi.");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#0d1b2a]/60 mb-1">{label}</span>
      <div className="flex gap-2 mb-2">
        <input
          className={inp + " flex-1"}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder="https://... veya aşağıdan dosya seç"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={loading}
          className="shrink-0 flex items-center gap-1.5 bg-[#0d1b2a] text-white text-xs font-bold px-3 py-2 rounded-md hover:bg-[#ffb703] hover:text-[#0d1b2a] transition-colors disabled:opacity-50"
        >
          {loading ? (
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
            </svg>
          )}
          {loading ? "Yükleniyor…" : "Dosya Seç"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="shrink-0 px-2 border border-[#0d1b2a]/15 rounded-md text-[#0d1b2a]/50 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
            title="Görseli kaldır"
          >
            <X size={14} />
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
      {value && (
        <div className="mt-2 rounded-lg overflow-hidden border border-[#0d1b2a]/10 bg-[#f6f6f6] flex items-center justify-center" style={{ maxHeight: 160 }}>
          <img src={value} alt="önizleme" className="max-h-40 max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}

function Btn({ onClick, children, variant = "primary", type = "button", disabled }) {
  const base = "inline-flex items-center gap-2 font-bold px-4 py-2 rounded-md text-sm transition-all disabled:opacity-50";
  const styles = {
    primary:   "bg-[#ffb703] text-[#0d1b2a] hover:scale-[1.02]",
    secondary: "bg-white border border-[#0d1b2a]/15 text-[#0d1b2a] hover:bg-[#f6f6f6]",
    danger:    "bg-red-50 border border-red-200 text-red-600 hover:bg-red-600 hover:text-white",
  };
  return <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${styles[variant]}`}>{children}</button>;
}

function Card({ children, className = "" }) {
  return <div className={`bg-white border border-[#0d1b2a]/10 rounded-xl p-6 ${className}`}>{children}</div>;
}

function SectionTitle({ children }) {
  return <h2 className="text-lg font-black text-[#0d1b2a] mb-5" style={{ fontFamily: "Chivo, Inter, sans-serif" }}>{children}</h2>;
}

function Toast({ msg, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 2500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fixed bottom-6 right-6 z-50 bg-[#0d1b2a] text-white px-5 py-3 rounded-xl shadow-2xl text-sm font-bold flex items-center gap-3">
      <span className="w-2 h-2 rounded-full bg-[#ffb703]" />
      {msg}
    </div>
  );
}

// ─── Login screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [err, setErr] = useState("");

  function submit(e) {
    e.preventDefault();
    if (adminLogin(user.trim(), pass)) { onLogin(); }
    else { setErr("Kullanıcı adı veya şifre hatalı."); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0d1b2a] p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <img src="/logo.png" alt="YGS" className="h-14 w-auto object-contain" />
        </div>
        <Card>
          <h1 className="text-2xl font-black text-[#0d1b2a] mb-1" style={{ fontFamily: "Chivo, Inter, sans-serif" }}>
            Yönetim Paneli
          </h1>
          <p className="text-sm text-[#0d1b2a]/50 mb-6">Devam etmek için giriş yapın.</p>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Kullanıcı Adı">
              <input type="text" required value={user} onChange={e => setUser(e.target.value)} className={inp} autoComplete="username" />
            </Field>
            <Field label="Şifre">
              <div className="relative">
                <input type={showPass ? "text" : "password"} required value={pass} onChange={e => setPass(e.target.value)} className={inp + " pr-10"} autoComplete="current-password" />
                <button type="button" onClick={() => setShowPass(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0d1b2a]/40 hover:text-[#0d1b2a]">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>
            {err && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">{err}</p>}
            <Btn type="submit" variant="primary">Giriş Yap</Btn>
          </form>
        </Card>
        <div className="mt-4 text-center">
          <Link to="/" className="text-xs text-white/50 hover:text-[#ffb703]">← Siteye Dön</Link>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Panel ───────────────────────────────────────────────────────────
function SettingsPanel({ onSave }) {
  const [d, setD] = useState(getSettings());
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  // band_items helpers
  const setBand = (i, v) => { const a = [...(d.band_items||[])]; a[i] = v; set("band_items", a); };
  const addBand = () => set("band_items", [...(d.band_items||[]), ""]);
  const delBand = (i) => set("band_items", (d.band_items||[]).filter((_,j)=>j!==i));

  // hero_badges helpers
  const setBadge = (i, k, v) => { const a = [...(d.hero_badges||[])]; a[i]={...a[i],[k]:v}; set("hero_badges",a); };
  const addBadge = () => set("hero_badges", [...(d.hero_badges||[]), { icon:"Shield", label:"" }]);
  const delBadge = (i) => set("hero_badges", (d.hero_badges||[]).filter((_,j)=>j!==i));

  // stats_items helpers
  const setStat = (i, k, v) => { const a = [...(d.stats_items||[])]; a[i]={...a[i],[k]:v}; set("stats_items",a); };
  const addStat = () => set("stats_items", [...(d.stats_items||[]), { value:"", label:"" }]);
  const delStat = (i) => set("stats_items", (d.stats_items||[]).filter((_,j)=>j!==i));

  function save() { saveSettings(d); onSave("Ayarlar kaydedildi."); }

  return (
    <div className="space-y-5">
      <SectionTitle>Site Ayarları</SectionTitle>

      {/* İletişim */}
      <Card>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#0d1b2a]/60 mb-4">İletişim Bilgileri</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Telefon"><input className={inp} value={d.phone||""} onChange={e=>set("phone",e.target.value)} /></Field>
          <Field label="2. Telefon (opsiyonel)"><input className={inp} value={d.phone2||""} onChange={e=>set("phone2",e.target.value)} placeholder="+90 212 555 0 145" /></Field>
          <Field label="E-posta"><input className={inp} value={d.email||""} onChange={e=>set("email",e.target.value)} /></Field>
          <Field label="WhatsApp Numarası (sadece rakam)"><input className={inp} value={d.whatsapp_phone||""} onChange={e=>set("whatsapp_phone",e.target.value)} placeholder="902125550144" /></Field>
          <Field label="WhatsApp İlk Mesaj (TR)"><input className={inp} value={d.whatsapp_message_tr||""} onChange={e=>set("whatsapp_message_tr",e.target.value)} placeholder="Merhaba YGS..." /></Field>
          <Field label="WhatsApp İlk Mesaj (EN)"><input className={inp} value={d.whatsapp_message_en||""} onChange={e=>set("whatsapp_message_en",e.target.value)} placeholder="Hi YGS..." /></Field>
          <Field label="Instagram URL"><input className={inp} value={d.instagram||""} onChange={e=>set("instagram",e.target.value)} /></Field>
          <Field label="LinkedIn URL"><input className={inp} value={d.linkedin||""} onChange={e=>set("linkedin",e.target.value)} /></Field>
          <Field label="Güven Rozeti Yazısı"><input className={inp} value={d.trust_badge_text||""} onChange={e=>set("trust_badge_text",e.target.value)} /></Field>
          <div className="md:col-span-2">
            <Field label="Adres"><input className={inp} value={d.address||""} onChange={e=>set("address",e.target.value)} /></Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Google Maps Embed URL"><input className={inp} value={d.map_embed_url||""} onChange={e=>set("map_embed_url",e.target.value)} /></Field>
          </div>
        </div>
      </Card>

      {/* Görseller */}
      <Card>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#0d1b2a]/60 mb-4">Görseller</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ImageField label="Hero Arka Plan Görseli" value={d.hero_image||""} onChange={v=>set("hero_image",v)} />
          <ImageField label="Hakkımızda Görseli"     value={d.about_image||""} onChange={v=>set("about_image",v)} />
        </div>
      </Card>

      {/* Kayan bant */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#0d1b2a]/60">Kayan Bant</h3>
            <p className="text-xs text-[#0d1b2a]/40 mt-0.5">Tüm öğeleri silersen bant gizlenir.</p>
          </div>
          <Btn variant="secondary" onClick={addBand}><Plus size={13} /> Ekle</Btn>
        </div>
        <div className="space-y-2">
          {(d.band_items||[]).map((item, i) => (
            <div key={i} className="flex gap-2">
              <input className={inp} value={item} onChange={e=>setBand(i,e.target.value)} placeholder="ISO 9001" />
              <button onClick={()=>delBand(i)} className="text-red-400 hover:text-red-600"><X size={16}/></button>
            </div>
          ))}
          {(d.band_items||[]).length === 0 && (
            <p className="text-xs text-[#0d1b2a]/40 py-2">Bant şu an gizli. Eklemek için "Ekle" butonuna bas.</p>
          )}
        </div>
      </Card>

      {/* Hero rozetler */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#0d1b2a]/60">Hero Rozetler</h3>
          <Btn variant="secondary" onClick={addBadge}><Plus size={13} /> Ekle</Btn>
        </div>
        <p className="text-xs text-[#0d1b2a]/40 mb-3">İkon: Shield, Wrench, Cpu, Star, Check, Award</p>
        <div className="space-y-2">
          {(d.hero_badges||[]).map((b, i) => (
            <div key={i} className="flex gap-2">
              <input className={inp} style={{maxWidth:120}} value={b.icon} onChange={e=>setBadge(i,"icon",e.target.value)} placeholder="Shield" />
              <input className={inp} value={b.label} onChange={e=>setBadge(i,"label",e.target.value)} placeholder="CE / TSE" />
              <button onClick={()=>delBadge(i)} className="text-red-400 hover:text-red-600"><X size={16}/></button>
            </div>
          ))}
        </div>
      </Card>

      {/* İstatistikler */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#0d1b2a]/60">İstatistikler</h3>
          <Btn variant="secondary" onClick={addStat}><Plus size={13} /> Ekle</Btn>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <Field label="Başlık"><input className={inp} value={d.stats_heading||""} onChange={e=>set("stats_heading",e.target.value)} /></Field>
          <Field label="Alt Yazı"><input className={inp} value={d.stats_sub||""} onChange={e=>set("stats_sub",e.target.value)} /></Field>
        </div>
        <div className="space-y-2">
          {(d.stats_items||[]).map((s, i) => (
            <div key={i} className="flex gap-2">
              <input className={inp} style={{maxWidth:120}} value={s.value} onChange={e=>setStat(i,"value",e.target.value)} placeholder="20+" />
              <input className={inp} value={s.label} onChange={e=>setStat(i,"label",e.target.value)} placeholder="Yıllık Deneyim" />
              <button onClick={()=>delStat(i)} className="text-red-400 hover:text-red-600"><X size={16}/></button>
            </div>
          ))}
        </div>
      </Card>

      <Btn onClick={save}><Save size={15} /> Kaydet</Btn>
    </div>
  );
}

// ─── Products Panel ───────────────────────────────────────────────────────────
const EMPTY_PRODUCT = { slug: "", title: "", short_desc: "", long_desc: "", hero_image: "", gallery: [], features: [], specs: [], order: 0, active: true, lang: "tr" };

function ProductsPanel({ onSave }) {
  const [items, setItems] = useState(getProducts());
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_PRODUCT);

  const refresh = () => setItems(getProducts());
  const startNew = () => { setDraft({ ...EMPTY_PRODUCT }); setEditing("new"); };
  const startEdit = (p) => { setDraft({ ...p }); setEditing(p.id); };
  const cancel = () => setEditing(null);

  function save() {
    if (editing === "new") addProduct(draft);
    else updateProduct(draft);
    refresh(); setEditing(null); onSave("Ürün kaydedildi.");
  }
  function del(id) {
    if (!window.confirm("Ürünü silmek istediğinize emin misiniz?")) return;
    deleteProduct(id); refresh(); onSave("Ürün silindi.");
  }

  const setF = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  const setFeature = (i, v) => { const a = [...draft.features]; a[i] = v; setF("features", a); };
  const addFeature = () => setF("features", [...draft.features, ""]);
  const delFeature = (i) => setF("features", draft.features.filter((_, j) => j !== i));
  const setSpec = (i, k, v) => { const a = [...draft.specs]; a[i] = { ...a[i], [k]: v }; setF("specs", a); };
  const addSpec = () => setF("specs", [...draft.specs, { label: "", value: "" }]);
  const delSpec = (i) => setF("specs", draft.specs.filter((_, j) => j !== i));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <SectionTitle>Ürünler</SectionTitle>
        <Btn onClick={startNew}><Plus size={15} /> Yeni Ürün</Btn>
      </div>

      {editing && (
        <Card className="border-2 border-[#ffb703]">
          <h3 className="font-black text-[#0d1b2a] mb-4">{editing === "new" ? "Yeni Ürün" : "Ürünü Düzenle"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Başlık"><input className={inp} value={draft.title} onChange={e => setF("title", e.target.value)} /></Field>
            <Field label="Slug (URL)"><input className={inp} value={draft.slug} onChange={e => setF("slug", e.target.value)} placeholder="otomatik-kapilar" /></Field>
            <div className="md:col-span-2">
              <Field label="Kısa Açıklama"><input className={inp} value={draft.short_desc} onChange={e => setF("short_desc", e.target.value)} /></Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Uzun Açıklama"><textarea className={ta} rows={4} value={draft.long_desc} onChange={e => setF("long_desc", e.target.value)} /></Field>
            </div>
            <div className="md:col-span-2">
              <ImageField label="Hero Görsel" value={draft.hero_image} onChange={v => setF("hero_image", v)} />
            </div>
            <Field label="Sıra"><input type="number" className={inp} value={draft.order} onChange={e => setF("order", Number(e.target.value))} /></Field>
            <Field label="Dil">
              <select className={inp} value={draft.lang} onChange={e => setF("lang", e.target.value)}>
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
              </select>
            </Field>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#0d1b2a]/60">Özellikler</span>
              <Btn variant="secondary" onClick={addFeature}><Plus size={13} /> Ekle</Btn>
            </div>
            <div className="space-y-2">
              {draft.features.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input className={inp} value={f} onChange={e => setFeature(i, e.target.value)} placeholder={`Özellik ${i + 1}`} />
                  <button onClick={() => delFeature(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#0d1b2a]/60">Teknik Özellikler</span>
              <Btn variant="secondary" onClick={addSpec}><Plus size={13} /> Ekle</Btn>
            </div>
            <div className="space-y-2">
              {draft.specs.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input className={inp} value={s.label} onChange={e => setSpec(i, "label", e.target.value)} placeholder="Etiket" />
                  <input className={inp} value={s.value} onChange={e => setSpec(i, "value", e.target.value)} placeholder="Değer" />
                  <button onClick={() => delSpec(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 flex gap-3">
            <Btn onClick={save}><Save size={15} /> Kaydet</Btn>
            <Btn variant="secondary" onClick={cancel}><X size={15} /> Vazgeç</Btn>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <Card key={p.id}>
            {p.hero_image && <div className="aspect-[16/9] rounded-lg overflow-hidden mb-4 bg-[#0d1b2a]"><img src={p.hero_image} alt={p.title} className="w-full h-full object-cover" /></div>}
            <div className="font-black text-[#0d1b2a]" style={{ fontFamily: "Chivo, Inter, sans-serif" }}>{p.title}</div>
            <div className="text-xs text-[#0d1b2a]/50 mt-1 mb-4 line-clamp-2">{p.short_desc}</div>
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={() => startEdit(p)}><Edit3 size={13} /> Düzenle</Btn>
              <Btn variant="danger" onClick={() => del(p.id)}><Trash2 size={13} /></Btn>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Services Panel ───────────────────────────────────────────────────────────
const EMPTY_SERVICE = { title: "", desc: "", icon: "Wrench", order: 0, active: true, lang: "tr" };

function ServicesPanel({ onSave }) {
  const [items, setItems] = useState(getServices());
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_SERVICE);

  const refresh = () => setItems(getServices());
  const startNew = () => { setDraft({ ...EMPTY_SERVICE }); setEditing("new"); };
  const startEdit = (s) => { setDraft({ ...s }); setEditing(s.id); };
  const cancel = () => setEditing(null);
  const setF = (k, v) => setDraft(p => ({ ...p, [k]: v }));

  function save() {
    if (editing === "new") addService(draft);
    else updateService(draft);
    refresh(); setEditing(null); onSave("Hizmet kaydedildi.");
  }
  function del(id) {
    if (!window.confirm("Hizmeti silmek istediğinize emin misiniz?")) return;
    deleteService(id); refresh(); onSave("Hizmet silindi.");
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <SectionTitle>Hizmetler</SectionTitle>
        <Btn onClick={startNew}><Plus size={15} /> Yeni Hizmet</Btn>
      </div>

      {editing && (
        <Card className="border-2 border-[#ffb703]">
          <h3 className="font-black text-[#0d1b2a] mb-4">{editing === "new" ? "Yeni Hizmet" : "Hizmeti Düzenle"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Başlık"><input className={inp} value={draft.title} onChange={e => setF("title", e.target.value)} /></Field>
            <Field label="İkon (Lucide adı)"><input className={inp} value={draft.icon} onChange={e => setF("icon", e.target.value)} placeholder="Wrench" /></Field>
            <div className="md:col-span-2">
              <Field label="Açıklama"><textarea className={ta} rows={3} value={draft.desc} onChange={e => setF("desc", e.target.value)} /></Field>
            </div>
            <Field label="Sıra"><input type="number" className={inp} value={draft.order} onChange={e => setF("order", Number(e.target.value))} /></Field>
            <Field label="Dil">
              <select className={inp} value={draft.lang} onChange={e => setF("lang", e.target.value)}>
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
              </select>
            </Field>
          </div>
          <div className="mt-4 flex gap-3">
            <Btn onClick={save}><Save size={15} /> Kaydet</Btn>
            <Btn variant="secondary" onClick={cancel}><X size={15} /> Vazgeç</Btn>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {items.map(s => (
          <Card key={s.id} className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-[#0d1b2a]">{s.title}</div>
              <div className="text-xs text-[#0d1b2a]/50 mt-1">{s.desc}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Btn variant="secondary" onClick={() => startEdit(s)}><Edit3 size={13} /></Btn>
              <Btn variant="danger" onClick={() => del(s.id)}><Trash2 size={13} /></Btn>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── FAQs Panel ───────────────────────────────────────────────────────────────
const EMPTY_FAQ = { question: "", answer: "", order: 0, active: true, lang: "tr" };

function FaqsPanel({ onSave }) {
  const [items, setItems] = useState(getFaqs());
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_FAQ);

  const refresh = () => setItems(getFaqs());
  const startNew = () => { setDraft({ ...EMPTY_FAQ }); setEditing("new"); };
  const startEdit = (f) => { setDraft({ ...f }); setEditing(f.id); };
  const cancel = () => setEditing(null);
  const setF = (k, v) => setDraft(p => ({ ...p, [k]: v }));

  function save() {
    if (editing === "new") addFaq(draft);
    else updateFaq(draft);
    refresh(); setEditing(null); onSave("SSS kaydedildi.");
  }
  function del(id) {
    if (!window.confirm("Soruyu silmek istediğinize emin misiniz?")) return;
    deleteFaq(id); refresh(); onSave("SSS silindi.");
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <SectionTitle>Sıkça Sorulan Sorular</SectionTitle>
        <Btn onClick={startNew}><Plus size={15} /> Yeni Soru</Btn>
      </div>

      {editing && (
        <Card className="border-2 border-[#ffb703]">
          <h3 className="font-black text-[#0d1b2a] mb-4">{editing === "new" ? "Yeni Soru" : "Soruyu Düzenle"}</h3>
          <div className="space-y-4">
            <Field label="Soru"><input className={inp} value={draft.question} onChange={e => setF("question", e.target.value)} /></Field>
            <Field label="Cevap"><textarea className={ta} rows={4} value={draft.answer} onChange={e => setF("answer", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sıra"><input type="number" className={inp} value={draft.order} onChange={e => setF("order", Number(e.target.value))} /></Field>
              <Field label="Dil">
                <select className={inp} value={draft.lang} onChange={e => setF("lang", e.target.value)}>
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                </select>
              </Field>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <Btn onClick={save}><Save size={15} /> Kaydet</Btn>
            <Btn variant="secondary" onClick={cancel}><X size={15} /> Vazgeç</Btn>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {items.map(f => (
          <Card key={f.id} className="flex items-start justify-between gap-4">
            <div>
              <div className="font-bold text-[#0d1b2a] text-sm">{f.question}</div>
              <div className="text-xs text-[#0d1b2a]/50 mt-1 line-clamp-2">{f.answer}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Btn variant="secondary" onClick={() => startEdit(f)}><Edit3 size={13} /></Btn>
              <Btn variant="danger" onClick={() => del(f.id)}><Trash2 size={13} /></Btn>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── References Panel ─────────────────────────────────────────────────────────
const EMPTY_REF = { name: "", logo_url: "", website: "", description: "", project_image: "", order: 0, active: true };

function ReferencesPanel({ onSave }) {
  const [items, setItems] = useState(getReferences());
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_REF);

  const refresh = () => setItems(getReferences());
  const startNew = () => { setDraft({ ...EMPTY_REF }); setEditing("new"); };
  const startEdit = (r) => { setDraft({ ...EMPTY_REF, ...r }); setEditing(r.id); };
  const cancel = () => setEditing(null);
  const setF = (k, v) => setDraft(p => ({ ...p, [k]: v }));

  function save() {
    if (editing === "new") addReference(draft);
    else updateReference(draft);
    refresh(); setEditing(null); onSave("Referans kaydedildi.");
  }
  function del(id) {
    if (!window.confirm("Referansı silmek istediğinize emin misiniz?")) return;
    deleteReference(id); refresh(); onSave("Referans silindi.");
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <SectionTitle>Referanslar</SectionTitle>
        <Btn onClick={startNew}><Plus size={15} /> Yeni Referans</Btn>
      </div>

      {editing && (
        <Card className="border-2 border-[#ffb703]">
          <h3 className="font-black text-[#0d1b2a] mb-4">{editing === "new" ? "Yeni Referans" : "Referansı Düzenle"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Firma Adı"><input className={inp} value={draft.name} onChange={e => setF("name", e.target.value)} /></Field>
            <Field label="Web Sitesi">
              <input
                className={inp}
                value={draft.website}
                onChange={e => setF("website", e.target.value)}
                placeholder="https://firma.com"
              />
              {draft.website && !draft.website.startsWith("http") && (
                <p className="text-xs text-orange-500 mt-1">⚠ URL https:// ile başlamalı</p>
              )}
            </Field>
            <Field label="Sıra"><input type="number" className={inp} value={draft.order} onChange={e => setF("order", Number(e.target.value))} /></Field>
            <div className="md:col-span-2">
              <Field label="Kısa Açıklama (referanslar sayfasında görünür)">
                <textarea className={ta} rows={2} value={draft.description || ""} onChange={e => setF("description", e.target.value)} placeholder="Yapılan iş hakkında kısa bilgi..." />
              </Field>
            </div>
            <div className="md:col-span-2">
              <ImageField label="Logo" value={draft.logo_url} onChange={v => setF("logo_url", v)} />
            </div>
            <div className="md:col-span-2">
              <ImageField label="Proje Görseli (referanslar sayfasında görünür)" value={draft.project_image || ""} onChange={v => setF("project_image", v)} />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <Btn onClick={save}><Save size={15} /> Kaydet</Btn>
            <Btn variant="secondary" onClick={cancel}><X size={15} /> Vazgeç</Btn>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map(r => (
          <Card key={r.id} className="text-center">
            {r.logo_url
              ? <img src={r.logo_url} alt={r.name} className="h-10 w-auto object-contain mx-auto mb-3" />
              : <div className="h-10 flex items-center justify-center mb-3 text-[#0d1b2a]/30 text-xs">Logo yok</div>
            }
            <div className="font-bold text-[#0d1b2a] text-sm">{r.name}</div>
            <div className="flex justify-center gap-2 mt-3">
              <Btn variant="secondary" onClick={() => startEdit(r)}><Edit3 size={13} /></Btn>
              <Btn variant="danger" onClick={() => del(r.id)}><Trash2 size={13} /></Btn>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── About Panel ──────────────────────────────────────────────────────────────
function AboutPanel({ onSave }) {
  const [d, setD] = useState(getAbout());
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const setBullet = (i, v) => { const a = [...d.bullets]; a[i] = v; set("bullets", a); };
  const addBullet = () => set("bullets", [...d.bullets, ""]);
  const delBullet = (i) => set("bullets", d.bullets.filter((_, j) => j !== i));

  function save() { saveAbout(d); onSave("Hakkımızda kaydedildi."); }

  return (
    <div className="space-y-5">
      <SectionTitle>Hakkımızda</SectionTitle>
      <Card>
        <div className="space-y-4">
          <Field label="Başlık"><input className={inp} value={d.heading} onChange={e => set("heading", e.target.value)} /></Field>
          <Field label="1. Paragraf"><textarea className={ta} rows={3} value={d.p1} onChange={e => set("p1", e.target.value)} /></Field>
          <Field label="2. Paragraf"><textarea className={ta} rows={3} value={d.p2} onChange={e => set("p2", e.target.value)} /></Field>
          <ImageField label="Görsel" value={d.image || ""} onChange={v => set("image", v)} />
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#0d1b2a]/60">Madde Listesi</span>
            <Btn variant="secondary" onClick={addBullet}><Plus size={13} /> Ekle</Btn>
          </div>
          <div className="space-y-2">
            {d.bullets.map((b, i) => (
              <div key={i} className="flex gap-2">
                <input className={inp} value={b} onChange={e => setBullet(i, e.target.value)} />
                <button onClick={() => delBullet(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-5">
          <Btn onClick={save}><Save size={15} /> Kaydet</Btn>
        </div>
      </Card>
    </div>
  );
}

// ─── Main Admin Shell ─────────────────────────────────────────────────────────
const TABS = [
  { id: "settings",   label: "Ayarlar",    icon: Settings  },
  { id: "products",   label: "Ürünler",    icon: Package   },
  { id: "services",   label: "Hizmetler",  icon: Wrench    },
  { id: "faqs",       label: "SSS",        icon: HelpCircle },
  { id: "references", label: "Referanslar",icon: Users     },
  { id: "about",      label: "Hakkımızda", icon: ExternalLink },
];

export default function Admin() {
  const [loggedIn, setLoggedIn] = useState(isAdminLoggedIn());
  const [tab, setTab] = useState("settings");
  const [toast, setToast] = useState(null);

  if (!loggedIn) return <LoginScreen onLogin={() => setLoggedIn(true)} />;

  function logout() { adminLogout(); setLoggedIn(false); }
  function showToast(msg) { setToast(msg); }

  function handleExport() {
    const content = exportSiteData();
    const blob = new Blob([content], { type: "text/javascript" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "siteData.js";
    a.click();
    URL.revokeObjectURL(url);
    showToast("siteData.js indirildi — src/data/ klasörüne koy, build al, Hostinger'a yükle.");
  }

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      {/* Header */}
      <header className="bg-[#0d1b2a] text-white sticky top-0 z-30 shadow-lg">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="YGS" className="h-9 w-auto object-contain" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#ffb703] font-bold hidden sm:block">
              Yönetim Paneli
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Export button */}
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 text-xs font-bold bg-[#ffb703] text-[#0d1b2a] px-3 py-2 rounded-md hover:scale-[1.02] transition-transform"
              title="Değişiklikleri build'e hazırla"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              <span className="hidden sm:inline">Siteyi Güncelle</span>
            </button>
            <Link to="/" target="_blank" className="text-xs text-white/60 hover:text-[#ffb703] flex items-center gap-1.5 px-2">
              <ExternalLink size={13} /> <span className="hidden sm:inline">Siteyi Gör</span>
            </Link>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md transition-colors"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">Çıkış</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex gap-6">
        {/* Sidebar */}
        <aside className="hidden md:flex flex-col gap-1 w-48 shrink-0">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-all text-left ${
                tab === t.id
                  ? "bg-[#0d1b2a] text-white"
                  : "text-[#0d1b2a]/60 hover:bg-white hover:text-[#0d1b2a]"
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </aside>

        {/* Mobile tab bar */}
        <div className="md:hidden w-full mb-4">
          <div className="flex gap-1 overflow-x-auto pb-1">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  tab === t.id ? "bg-[#0d1b2a] text-white" : "bg-white text-[#0d1b2a]/60"
                }`}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <main className="flex-1 min-w-0">
          {tab === "settings"   && <SettingsPanel   onSave={showToast} />}
          {tab === "products"   && <ProductsPanel   onSave={showToast} />}
          {tab === "services"   && <ServicesPanel   onSave={showToast} />}
          {tab === "faqs"       && <FaqsPanel       onSave={showToast} />}
          {tab === "references" && <ReferencesPanel onSave={showToast} />}
          {tab === "about"      && <AboutPanel      onSave={showToast} />}
        </main>
      </div>

      {toast && <Toast msg={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
